import { handleError, hasError, tryCatch, unknownError } from '@repo/shared/api/helpers'
import { CaseWorkersRoutes } from '@repo/shared/routes-case-workers'
import { AxiosInstance } from 'axios'

import { buildCaseWorkerFromDto } from '@/external/api/helpers'
import { mockedData } from '@/store/mock/mock'
import { IApi } from '@/store/spi/api'

type T = {
  admin: IApi['admin']['caseWorkers']
  app: IApi['caseWorkers']
}

export const apiCaseWorkers: (api?: AxiosInstance) => T = (api): T => ({
  //
  // ADMIN
  //
  admin: {
    patchOne: api
      ? async ({ caseWorkerId, organizationId, fields }) => {
          const res = await tryCatch(
            async () =>
              await api.patch<CaseWorkersRoutes.patchOne.Response>(
                CaseWorkersRoutes.patchOne.call({
                  caseWorkerId,
                  organizationId,
                }),
                { fields } satisfies CaseWorkersRoutes.patchOne.Request,
              ),
          )
          if (hasError(res)) return handleError(res)
          return buildCaseWorkerFromDto(res.caseWorker)
        }
      : (p) => {
          const caseWorker = mockedData.caseWorker
          return Promise.resolve({ ...caseWorker, ...p.fields })
        },

    disableOrEnableOne: api
      ? async ({ caseWorkerId, organizationId, disabled }) => {
          const res = await tryCatch(
            async () =>
              await api.patch<CaseWorkersRoutes.disableOrEnableOne.Response>(
                CaseWorkersRoutes.disableOrEnableOne.call({
                  caseWorkerId,
                  organizationId,
                }),
                {
                  disabled,
                } satisfies CaseWorkersRoutes.disableOrEnableOne.Request,
              ),
          )
          if (hasError(res)) return handleError(res)
          return buildCaseWorkerFromDto(res.caseWorker)
        }
      : (p) => {
          const caseWorker = mockedData.caseWorker
          if (p.caseWorkerId === caseWorker.id) {
            return Promise.resolve({
              ...caseWorker,
              isDisabled: p.disabled,
            })
          }
          return Promise.resolve(unknownError)
        },

    inviteOne: api
      ? async ({ organizationId, fields }) => {
          const caseWorkersRes = await tryCatch(
            async () =>
              await api.post<CaseWorkersRoutes.inviteOne.Response>(
                CaseWorkersRoutes.inviteOne.call({
                  organizationId,
                }),
                {
                  fields,
                },
              ),
          )
          if (hasError(caseWorkersRes)) return handleError(caseWorkersRes)
          return buildCaseWorkerFromDto(caseWorkersRes.caseWorker)
        }
      : (p) => {
          const caseWorker = mockedData.caseWorker
          return Promise.resolve({ ...caseWorker, ...p.fields })
        },
  },

  //
  // APP
  //
  app: {
    get: api
      ? async ({ organizationId }) => {
          const res = await tryCatch(
            async () =>
              await api.get<CaseWorkersRoutes.get.Response>(
                CaseWorkersRoutes.get.call({ organizationId }),
              ),
          )
          if (hasError(res)) return handleError(res)
          return res.caseWorkers.map(buildCaseWorkerFromDto)
        }
      : () => {
          const cms = mockedData.caseWorkers
          return Promise.resolve(cms)
        },

    getOne: api
      ? async ({ caseWorkerId, organizationId }) => {
          const res = await tryCatch(
            async () =>
              await api.get<CaseWorkersRoutes.getOne.Response>(
                CaseWorkersRoutes.getOne.call({ caseWorkerId, organizationId }),
              ),
          )
          if (hasError(res)) return handleError(res)
          return buildCaseWorkerFromDto(res.caseWorker)
        }
      : (p) => {
          const caseWorker = mockedData.caseWorker
          if (caseWorker.id === p.caseWorkerId) return Promise.resolve(caseWorker)
          return Promise.resolve(unknownError)
        },

    getCurrent: api
      ? async ({ organizationId }) => {
          const caseWorkerRes = await tryCatch(
            async () =>
              await api.get<CaseWorkersRoutes.getCurrent.Response>(
                CaseWorkersRoutes.getCurrent.call({ organizationId }),
              ),
          )
          if (hasError(caseWorkerRes)) return handleError(caseWorkerRes)
          return buildCaseWorkerFromDto(caseWorkerRes.caseWorker)
        }
      : () => {
          const caseWorker = mockedData.caseWorker
          return Promise.resolve(caseWorker)
        },
  },
})
